/**
 * @fileoverview Hook for pinging activity status to keep session alive
 *
 * @example
 * // Usage:
 * import { useActivityPing } from './useActivityPing'
 *
 * function Component() {
 *   const ping = useActivityPing()
 *
 *   useEffect(() => {
 *     // Ping every 5 minutes
 *     const interval = setInterval(ping, 5 * 60 * 1000)
 *     return () => clearInterval(interval)
 *   }, [])
 * }
 */


import axios, { AxiosError } from 'axios'
import { useContext } from 'react'
import { DataContext } from '../DataContext'

/**
 * @function isAssignmentExpired
 * @description Checks if the assignment is expired
 * @param {number} status - The status code
 * @returns {boolean} True if the assignment is expired, false otherwise
 */
function isAssignmentExpired(status: number): boolean {
    return status === 408
}

/**
 * @function useActivityPing
 * @description Hook for pinging activity status to keep session alive
 * @returns {Function} A function that returns a Promise<void>
 */
export function useActivityPing() {
    const { setIsExpired } = useContext(DataContext)
    return async function updateActivityTimer() {
        try {
            await axios.post(`${process.env.API_HOST}/time`)
        } catch (e: unknown) {
            const status = (e as AxiosError).response?.status
            if (status && isAssignmentExpired(status)) {
                setIsExpired(true)
            }
        }
        return Promise.resolve()
    }
}
